import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

export const CTASection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true });
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const subject = `Contact from ${formState.name}`;
    const body = `Name: ${formState.name}\nEmail: ${formState.email}\n\nMessage:\n${formState.message}`;
    const mailtoLink = `mailto:info@ugw-global.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.location.href = mailtoLink;
    
    setIsSubmitting(false);
    setFormState({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" ref={containerRef} className="py-24 bg-white">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-16">
          
          {/* Left Side: Commitment & Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h4 className="text-amber-500 font-bold uppercase tracking-widest text-sm mb-2">Our Commitment</h4>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
              Unlock Value Through Clarity, Expertise, and Integrity
            </h2>
            <p className="text-zinc-600 text-lg mb-12 leading-relaxed">
              Our goal is simple: to enable clients to operate confidently and successfully in complex, capital-intensive markets. At UGW GLOBAL, we don’t just facilitate transactions—we engineer success through strategic clarity, economic-market intelligence, and operational rigor.
            </p>

            <div className="space-y-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-zinc-100 rounded-sm flex items-center justify-center text-zinc-900 mr-6 shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-1">Email Us</h3>
                  <p className="text-zinc-600">info@ugw-global.com</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-12 h-12 bg-zinc-100 rounded-sm flex items-center justify-center text-zinc-900 mr-6 shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-1">Global Headquarters</h3>
                  <p className="text-zinc-600">9, Rydal Close, Holders Hill Road.London. NW4 1LE</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side: Contact Form */}
          <motion.div
            className="bg-zinc-50 p-8 md:p-10 rounded-sm border border-zinc-100 shadow-lg"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="text-2xl font-bold text-zinc-900 mb-6">Start the Conversation</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">Name</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                  placeholder="Your Name"
                  value={formState.name}
                  onChange={(e) => setFormState({...formState, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">Email</label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                  placeholder="your@email.com"
                  value={formState.email}
                  onChange={(e) => setFormState({...formState, email: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-2 uppercase tracking-wide">Message</label>
                <textarea
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors resize-none"
                  placeholder="How can we help you?"
                  value={formState.message}
                  onChange={(e) => setFormState({...formState, message: e.target.value})}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-zinc-900 text-white font-bold uppercase tracking-widest py-4 px-6 rounded-sm hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>

        </div>
      </div>
    </section>
  );
};